"use client";

import { putFetchActiveExperiments } from "@hopper-b2b/api";
import {
  ExperimentState,
  ExperimentsType,
  IApiConfig,
} from "@hopper-b2b/types";
import { ISessionContextProps } from "@hopper-b2b/utilities";
import { HOPPER_PRO_COPY_COMPLIANCE } from "./consumerCopyCompliance";
import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
  useRef,
} from "react";

export const HIDE_CC = "ctr-marketplace-hidecreditcard";

export type UserExperiments = ExperimentState & {
  initialized: boolean;
};

const defaultInitState: UserExperiments = {
  initialized: false,
  experiments: {},
  trackingProperties: undefined,
};

export const ExperimentsContext =
  createContext<UserExperiments>(defaultInitState);

type ExperimentsProviderProps = {
  apiConfig: IApiConfig;
  sessionContext?: ISessionContextProps;
  isServerSideRendering?: boolean;
  initialExperiments?: ExperimentsType;
  loadingFallback: React.ReactNode;
  /**
   * Render children before the session exists (server included), with the
   * initial experiments, and apply the session's experiments in place when
   * they arrive instead of swapping to `loadingFallback`. Off by default;
   * seo-site sets it for marketing pages only, whose server-rendered HTML
   * must not depend on a session.
   */
  renderWithoutSession?: boolean;
};

export const ExperimentsProvider: FC<
  ExperimentsProviderProps & PropsWithChildren
> = ({
  apiConfig,
  sessionContext,
  isServerSideRendering = false,
  initialExperiments,
  children,
  loadingFallback,
  renderWithoutSession = false,
}) => {
  const hasSession = !!sessionContext;

  const [clientInitialized, setClientInitialized] = useState(
    (isServerSideRendering && hasSession) || defaultInitState.initialized
  );
  const [experiments, setExperiments] = useState<ExperimentsType>(
    initialExperiments || defaultInitState.experiments
  );
  const [trackingProperties, setTrackingProperties] = useState(
    defaultInitState.trackingProperties
  );
  const firstEffect = useRef(true);

  const noSessionYet = !hasSession;

  useEffect(() => {
    const useServerExperiments = firstEffect.current && isServerSideRendering;
    firstEffect.current = false;
    if (noSessionYet) {
      setExperiments((current) => ({
        ...current,
        [HOPPER_PRO_COPY_COMPLIANCE]: "Control",
      }));
      return;
    }
    if (useServerExperiments) return;

    setExperiments((current) => ({
      ...current,
      [HOPPER_PRO_COPY_COMPLIANCE]: "Control",
    }));
    let cancelled = false;

    Promise.resolve()
      .then(() => {
        if (!cancelled && !renderWithoutSession) setClientInitialized(false);
        return putFetchActiveExperiments(apiConfig);
      })
      .then((newState: ExperimentState) => {
        if (cancelled) return;
        setExperiments(
          (currentState: ExperimentsType) =>
            ({
              ...currentState,
              ...newState.experiments,
              [HOPPER_PRO_COPY_COMPLIANCE]:
                newState.experiments[HOPPER_PRO_COPY_COMPLIANCE] ?? "Control",
            }) as ExperimentsType
        );
        setTrackingProperties({
          experiments: Object.keys(newState.experiments).map(
            (key: string) => `${key}_${newState.experiments[key]}`
          ),
        });
      })
      .catch(() => {
        // Keep the safe Control variant when refreshing experiments fails.
      })
      .finally(() => {
        if (!cancelled) setClientInitialized(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    apiConfig,
    noSessionYet,
    isServerSideRendering,
    renderWithoutSession,
    sessionContext,
  ]);

  return (
    <ExperimentsContext.Provider
      value={{
        initialized: clientInitialized,
        experiments,
        trackingProperties,
      }}
      children={
        clientInitialized || renderWithoutSession ? children : loadingFallback
      }
    />
  );
};
